/**
 * Environment detection utility
 * Extracts environment and region from the current URL to construct API gateway URLs
 */

export interface EnvironmentConfig {
  environment: string;
  region: string;
}

/**
 * Loopback hostnames a dev server can be reached on. `127.0.0.1` and `[::1]`
 * matter as much as `localhost`: the webpack dev server prints them as
 * alternatives, and a browser that resolved `localhost` to IPv6 shows `[::1]` in
 * the address bar. Missing them meant the app treated a local page as
 * production, skipped the mock data service, and rendered empty pages.
 */
const LOOPBACK_HOSTNAMES = ['localhost', '0.0.0.0', '127.0.0.1', '::1', '[::1]'];

/** Shared local/dev-desktop hostname test — see isLocalEnvironment. */
const isLocalHostname = (hostname: string): boolean =>
  LOOPBACK_HOSTNAMES.includes(hostname)
  || hostname.startsWith('192.168.')
  || hostname.endsWith('.local')
  || hostname.startsWith('dev-dsk-');

/**
 * Extracts environment and region from the current URL
 *
 * URL Pattern: https://{environment}.{region}.command-center.rnr.amazon.dev/...
 * Examples:
 * - https://beta.us-east-1.command-center.rnr.amazon.dev/ → { environment: 'beta', region: 'us-east-1' }
 * - https://gamma.us-west-2.command-center.rnr.amazon.dev/ → { environment: 'gamma', region: 'us-west-2' }
 * - localhost:3000 → { environment: 'beta', region: 'us-east-1' } (defaults)
 */
export function detectEnvironment(): EnvironmentConfig {
  const defaultConfig: EnvironmentConfig = {
    environment: 'gamma',
    region: 'us-east-1'
  };

  try {
    const hostname = window.location.hostname;

    // Handle local development environments (including dev-dsk cloud desktops)
    if (isLocalHostname(hostname)) {
      console.log('[Environment Detection] Local environment detected, using defaults:', defaultConfig);
      return defaultConfig;
    }

    // Parse production-like URLs
    // Expected pattern: {environment}.{region}.command-center.rnr.amazon.dev
    const parts = hostname.split('.');
    
    if (parts.length >= 2) {
      const environment = parts[0]; // First part is the environment (beta, gamma, prod, etc.)
      const region = parts[1]; // Second part is the region (us-east-1, us-west-2, etc.)
      
      // Validate that we have reasonable values
      if (environment && region) {
        const config: EnvironmentConfig = {
          environment,
          region
        };
        
        console.log('[Environment Detection] Detected environment from URL:', config);
        return config;
      }
    }

    console.log('[Environment Detection] Could not parse URL, using defaults:', defaultConfig);
    return defaultConfig;

  } catch (error) {
    console.warn('[Environment Detection] Error detecting environment, using defaults:', error);
    return defaultConfig;
  }
}

/**
 * Constructs the API gateway URL based on the current environment
 *
 * @returns The complete API gateway URL
 */
export function getApiGatewayUrl(): string {

  const prefix = process.env.API_GATEWAY_PREFIX || 'https://api.';
  const suffix = process.env.API_GATEWAY_SUFFIX || '.rcc-gateway.returns.amazon.dev';
  
  const { environment, region } = detectEnvironment();
  
  // Construct URL: {prefix}{environment}.{region}{suffix}
  const apiUrl = `${prefix}${environment}.${region}${suffix}`;
  
  console.log('[Environment Detection] Constructed API Gateway URL:', apiUrl);
  return apiUrl;
}

/**
 * Checks if the application is running in a local development environment.
 * Matches the loopback hostnames (localhost, 0.0.0.0, 127.0.0.1, ::1),
 * 192.168.x LAN addresses, .local domains, and dev-dsk-* cloud desktop
 * hostnames.
 *
 * @returns true if running locally or on a cloud desktop, false otherwise
 */
export function isLocalEnvironment(): boolean {
  try {
    return isLocalHostname(window.location.hostname);
  } catch (error) {
    console.warn('[Environment Detection] Error checking local environment:', error);
    return false;
  }
}

/**
 * Checks whether this app is running inside an iframe (i.e. embedded by the
 * Boson shell) versus loaded directly as a top-level browser tab (e.g. a
 * personal stack or a standalone same-origin deploy).
 *
 * The app only reaches its backend through the Boson parent window
 * (see services/boson/bosonClient.tsx). When it IS framed, the Boson shell
 * already holds the user's Midway/TCP session and supplies identity over
 * postMessage, so the child performs no auth of its own. When there is NO
 * parent frame, no shell supplies auth, so the app must authenticate the user
 * itself (see services/midwayAuth.ts).
 *
 * Detection: a top-level document has `window.self === window.top`. When the
 * page is framed by a CROSS-ORIGIN parent, merely reading `window.top` throws
 * a SecurityError — that throw is itself proof we are framed, so we treat it
 * as `true`.
 *
 * NOTE: this reports FRAMED vs NOT FRAMED, not WHO the parent is. "Framed" is
 * the ONLY browser-observable signal for iframe-vs-direct — it cannot be seen
 * at the CloudFront/CDK edge (no request header carries it), so the auth gate
 * for the direct case must live here in the client.
 *
 * @returns true if running inside an iframe, false if a top-level tab
 */
export function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch (error) {
    // Cross-origin access to window.top throws — that itself means we are framed.
    console.warn('[Environment Detection] window.top access threw; treating as framed:', error);
    return true;
  }
}

/**
 * Deploy-time flag set by RCCFrontendCDK (`MidwayEdgeAuth` in the
 * KatalDeploymentLambda deployTimeReplacements): "true" on stages where
 * CloudFront runs the Midway Lambda@Edge on every request, "false" elsewhere.
 * KatalWebpackPlugin swaps the template for that value at deploy time; an
 * unreplaced template (local dev, or a stage that does not pass the key) reads
 * as "not enabled", so the browser-side gate keeps running.
 */
const MIDWAY_EDGE_AUTH_TEMPLATE = 'false';
/** Global read first so tests can set it, and so the minifier cannot fold the template away at build time. */
const MIDWAY_EDGE_AUTH_GLOBAL = '__RCC_MIDWAY_EDGE_AUTH__';

/**
 * True when CloudFront already enforces Midway for this site. The edge gate and
 * the browser-side gate both use the Midway ?id_token= redirect on the same
 * URL; the edge consumes the token first, so the browser gate never gets one
 * and stops at "Sign-in could not finish". With the edge gate on, nobody
 * receives the app without a Midway session, so the browser gate is redundant.
 */
export function isMidwayEdgeAuthEnabled(): boolean {
  const injected = (globalThis as Record<string, unknown>)[MIDWAY_EDGE_AUTH_GLOBAL];
  const value = typeof injected === 'string' ? injected : MIDWAY_EDGE_AUTH_TEMPLATE;
  return value === 'true';
}

/**
 * Decides whether this page load must run the client-side Midway sign-in gate
 * (see services/midwayAuth.ts) before the app renders.
 *
 * The gate runs for EVERY top-level browser load except local dev:
 *  - NOT framed — a framed page is the Boson shell, which supplies auth itself
 *    (and Midway cannot be framed, so a redirect there would fail anyway);
 *  - NOT local/dev — local dev serves mock data and has no Midway host, so
 *    gating it would only break the local workflow.
 *
 * Every other top-level host is gated — including the raw CloudFront domain
 * (`*.cloudfront.net`) and any preview/unlisted host — so a user who reaches
 * the app anywhere other than inside Boson is sent through Midway first.
 *
 * TRADE-OFF (intended): the Midway redirect uses `client_id = window.location
 * .host`, which only SUCCEEDS on a host registered with Midway (the deployed
 * command-center domains, e.g. `beta.us-east-1.command-center.rnr.amazon.dev`,
 * and the personal-stack `*.dev.command-center.rnr.amazon.dev`). On an
 * UNREGISTERED host (raw `*.cloudfront.net`, a preview URL) Midway rejects the
 * client, the loop guard trips after a couple of bounces, and the user lands on
 * the inline "sign-in required" message instead of the app. That is the desired
 * outcome here: an unregistered front door should NOT serve the app. Note this
 * is a client-side deterrent, not a true access boundary — someone who skips the
 * JS can still read the served bundle; hard enforcement belongs at the edge.
 *
 * @returns true if the Midway sign-in gate should run before render
 */
export function requiresDirectBrowserAuth(): boolean {
  try {
    if (isMidwayEdgeAuthEnabled()) return false;
    if (isInIframe()) return false;
    if (isLocalEnvironment()) return false;
    return true;
  } catch (error) {
    // If we cannot even read location/frame state, do not block rendering.
    console.warn('[Environment Detection] Error deciding direct-browser auth; not gating:', error);
    return false;
  }
}
